import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '@/components/Portal';
import { useDismissable } from '@/lib/useDismissable';
import { useDmFolders } from '@/lib/useDmFolders';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { getChannelLevel, setChannelLevel, subscribeMuteSettings } from '@/lib/muteSettings';
import {
  BellIcon,
  BellOffIcon,
  CheckIcon,
  CloseIcon,
  FolderIcon,
  PlusIcon,
  TrashIcon,
} from '@/components/icons';

/**
 * Discord/Telegram-style right-click menu for a DM row. Cursor-anchored
 * (Portal + useLayoutEffect clamp, mirroring ServerContextMenu).
 *
 * Sections: notifications (mute ↔ unmute, channel-level via muteSettings →
 * existing /api/channels/:id/mute), add-to-folder (checkbox toggles + inline
 * "new folder"), and the destructive pair — clear history (wipes messages for
 * everyone) and delete chat (group → leave; 1:1 → clear + drop from my list).
 * The destructive items use an inline two-step confirm so a mis-click can't
 * nuke a conversation. The self-vault hides notifications + delete.
 */
export function DmFolderContextMenu(props: {
  channelId: string;
  x: number;
  y: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirm, setConfirm] = useState<null | 'clear' | 'delete'>(null);
  const [busy, setBusy] = useState(false);
  const { folders, toggleEntry, createFolder, isMutating } = useDmFolders();

  const dm = useDataStore((s) => s.dmChannels[props.channelId]);
  const removeDmChannel = useDataStore((s) => s.removeDmChannel);
  const clearChannelMessages = useDataStore((s) => s.clearChannelMessages);
  const isGroup = dm?.type === 'GROUP_DM';
  // Self-vault ("Сейф") = a DM with only me; it can't be muted or deleted.
  const isVault = !isGroup && (dm?.directParticipants?.length ?? 0) <= 1;

  const muteLevel = useSyncExternalStore(
    subscribeMuteSettings,
    () => getChannelLevel(props.channelId, null),
    () => 'ALL' as const,
  );
  const muted = muteLevel === 'NOTHING';

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 8;
    let nx = props.x;
    let ny = props.y;
    if (nx + r.width + m > window.innerWidth) nx = props.x - r.width;
    if (ny + r.height + m > window.innerHeight) ny = window.innerHeight - r.height - m;
    nx = Math.min(Math.max(m, nx), window.innerWidth - r.width - m);
    ny = Math.max(m, ny);
    setPos({ x: nx, y: ny });
  }, [props.x, props.y, creating, confirm, folders.length]);

  useDismissable(true, ref, props.onClose);

  async function submitNew() {
    const name = newName.trim();
    if (!name) return;
    try {
      const folder = await createFolder(name);
      toggleEntry(folder.id, props.channelId, true);
    } finally {
      setNewName('');
      setCreating(false);
      props.onClose();
    }
  }

  async function doClear() {
    if (busy) return;
    setBusy(true);
    try {
      await api.clearDirectMessageHistory(props.channelId);
      clearChannelMessages(props.channelId);
      toast.success(t('dm.historyCleared'));
      props.onClose();
    } catch {
      toast.error(t('dm.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (busy) return;
    setBusy(true);
    try {
      // Group: leave it. 1:1: hide it from MY list only — history and the other
      // person's view stay intact; it re-surfaces if they write again or I
      // reopen it (server-persisted, so it stays gone across reloads).
      if (isGroup) await api.leaveGroupDirectMessage(props.channelId);
      else await api.hideDirectMessage(props.channelId);
      removeDmChannel(props.channelId);
      toast.success(t('dm.chatDeleted'));
      props.onClose();
    } catch {
      toast.error(t('dm.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  const positioned = pos
    ? { left: pos.x, top: pos.y }
    : ({ left: -9999, top: -9999, visibility: 'hidden' } as const);

  return (
    <Portal>
      <div
        ref={ref}
        style={positioned}
        className="bg-surface fixed z-[100] min-w-[230px] max-w-[290px] overflow-hidden rounded-xl border border-separator py-1.5 shadow-card"
        role="menu"
      >
        {confirm ? (
          <div className="px-3 py-2">
            <p className="mb-2 text-sm text-ink">
              {confirm === 'clear' ? t('dm.clearHistoryConfirm') : t('dm.deleteChatConfirm')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirm(null)}
                className="rounded-lg px-2.5 py-1 text-xs text-ink-muted transition-colors hover:text-ink disabled:opacity-50"
              >
                {t('dm.confirmCancel')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void (confirm === 'clear' ? doClear() : doDelete())}
                className="font-medium rounded-lg bg-danger/15 px-2.5 py-1 text-xs text-danger transition-colors hover:bg-danger/25 disabled:opacity-50"
              >
                {confirm === 'clear' ? t('dm.clearHistory') : t('dm.deleteChat')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Notifications — channel-level mute toggle (no guild fallback for DMs). */}
            {!isVault && (
              <MenuItem
                icon={muted ? <BellIcon className="h-4 w-4" /> : <BellOffIcon className="h-4 w-4" />}
                onClick={() => {
                  setChannelLevel(props.channelId, muted ? 'ALL' : 'NOTHING');
                  toast.success(muted ? t('dm.notificationsOn') : t('dm.chatMuted'));
                  props.onClose();
                }}
              >
                {muted ? t('dm.enableNotifications') : t('dm.muteChat')}
              </MenuItem>
            )}

            {!isVault && <div className="h-2" role="separator" />}

            {/* Add to folder */}
            <div className="px-3 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              {t('dm.addToFolder')}
            </div>
            {folders.length === 0 && !creating && (
              <div className="px-3 py-1.5 text-xs text-ink-muted">{t('dm.noFolders')}</div>
            )}
            {folders.map((f) => {
              const member = f.channelIds.includes(props.channelId);
              return (
                <button
                  key={f.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={member}
                  disabled={isMutating}
                  onClick={() => {
                    toggleEntry(f.id, props.channelId, !member);
                    props.onClose();
                  }}
                  className="group/ctx mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm text-ink transition-colors hover:bg-white/8 disabled:opacity-50"
                >
                  <FolderIcon className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover/ctx:text-accent" />
                  <span className="flex-1 truncate">{f.name}</span>
                  {member && <CheckIcon className="h-4 w-4 shrink-0 text-accent" />}
                </button>
              );
            })}
            {creating ? (
              <form
                className="px-2 py-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitNew();
                }}
              >
                <input
                  ref={inputRef}
                  autoFocus
                  value={newName}
                  maxLength={40}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setCreating(false);
                      setNewName('');
                    }
                  }}
                  placeholder={t('dm.folderName')}
                  className="w-full rounded-lg border border-separator bg-default/40 px-2.5 py-1.5 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent"
                />
              </form>
            ) : (
              <MenuItem icon={<PlusIcon className="h-4 w-4" />} onClick={() => setCreating(true)}>
                {t('dm.newFolderItem')}
              </MenuItem>
            )}

            <div className="h-2" role="separator" />

            {/* Destructive — inline-confirmed */}
            <MenuItem
              icon={<CloseIcon className="h-4 w-4" />}
              danger
              onClick={() => setConfirm('clear')}
            >
              {t('dm.clearHistory')}
            </MenuItem>
            {!isVault && (
              <MenuItem
                icon={<TrashIcon className="h-4 w-4" />}
                danger
                onClick={() => setConfirm('delete')}
              >
                {isGroup ? t('dm.leaveGroup') : t('dm.deleteChat')}
              </MenuItem>
            )}
          </>
        )}
      </div>
    </Portal>
  );
}

function MenuItem(props: {
  icon?: React.ReactNode;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={props.onClick}
      className={
        'group/ctx mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm transition-colors hover:bg-white/8 ' +
        (props.danger ? 'text-danger' : 'text-ink')
      }
    >
      {props.icon && (
        <span
          className={
            'shrink-0 transition-colors ' +
            (props.danger ? 'text-danger' : 'text-ink-muted group-hover/ctx:text-accent')
          }
        >
          {props.icon}
        </span>
      )}
      <span className="flex-1 truncate">{props.children}</span>
    </button>
  );
}
